import React from 'react';
import PropTypes from 'prop-types';
import {injectIntl} from 'react-intl';
import {ICONS} from '../../lib/curo-icons.js';

/**
 * 에러 로그 뷰어 (지원용) — Ctrl+Alt+E 로 열림. 학생에게는 안 보임(단축키만).
 * 기록은 index.ejs 최상단 워치독이 localStorage('curoai_errors')에 남김 — 서버 전송 0.
 * 용도: "안 돼요" 문의 시 교사/지원이 최근 에러를 복사해 전달. (LED 8×8 2주 잠복 재발 방지)
 * 2026-07-23 신설. 지원 도구라 문구는 ko/en만(학생 대면 UI 아님).
 * 2026-10-16: 워치독이 실패한 네트워크 요청도 남긴다("network: HTTP 404 GET …"). 같은 줄이 반복되면 n 으로 횟수만 셈(시각은 마지막 발생).
 */

const KEY = 'curoai_errors';
const MSG = {
    title: {en: 'Recent errors (support)', ko: '최근 에러 (지원용)'},
    empty: {en: 'No errors recorded — good sign.', ko: '기록된 에러 없음 — 좋은 신호예요.'},
    copy: {en: 'Copy', ko: '복사'},
    copied: {en: 'Copied', ko: '복사됨'},
    clear: {en: 'Clear', ko: '지우기'},
    close: {en: 'Close', ko: '닫기'},
    note: {en: 'Stored only on this device. Nothing is sent to a server.', ko: '이 기기에만 저장돼요. 서버로 전송되지 않습니다.'}
};

class ErrorLog extends React.Component {
    constructor (props) {
        super(props);
        this.state = {open: false, rows: [], copied: false};
        this._onKey = this._onKey.bind(this);
        this.close = this.close.bind(this);
    }
    componentDidMount () { window.addEventListener('keydown', this._onKey); }
    componentWillUnmount () { window.removeEventListener('keydown', this._onKey); }
    _onKey (e) {
        // Ctrl+Alt+E (브라우저 기본 단축키와 충돌 적은 조합)
        if (e.ctrlKey && e.altKey && (e.key === 'e' || e.key === 'E')) {
            e.preventDefault();
            this.setState(s => (s.open ? {open: false} : {open: true, rows: this._read(), copied: false}));
        }
    }
    _read () {
        try { return JSON.parse(localStorage.getItem(KEY) || '[]').slice().reverse(); } catch (e) { return []; }
    }
    _text () {
        return this.state.rows.map(r => {
            const d = new Date(r.t);
            const pad = n => `0${n}`.slice(-2);
            const ts = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
            return `[${ts}] ${r.m}${r.n > 1 ? ` (x${r.n})` : ''}${r.s ? ` @${r.s}:${r.l}` : ''}${r.k ? `\n    ${r.k}` : ''}`;
        }).join('\n');
    }
    _copy () {
        const txt = `Curo AI ${navigator.userAgent}\n${location.href}\n\n${this._text()}`;
        const done = () => { this.setState({copied: true}); setTimeout(() => this.setState({copied: false}), 1800); };
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(txt).then(done, () => { /* noop */ });
                return;
            }
        } catch (e) { /* noop */ }
        try {
            const ta = document.createElement('textarea');
            ta.value = txt;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
            done();
        } catch (e) { /* noop */ }
    }
    _clear () {
        try { localStorage.removeItem(KEY); } catch (e) { /* noop */ }
        this.setState({rows: []});
    }
    close () { this.setState({open: false}); }
    t (key) {
        const loc = (this.props.intl && this.props.intl.locale) || 'en';
        const m = MSG[key];
        if (!m) return key;
        return (loc.indexOf('ko') === 0 ? m.ko : m.en) || m.en;
    }
    render () {
        if (!this.state.open) return null;
        const {rows} = this.state;
        return (
            <div style={S.overlay} onClick={this.close}>
                <div style={S.modal} onClick={e => e.stopPropagation()}>
                    <div style={S.head}>
                        <span style={S.title}>{this.t('title')}<span style={S.cnt}>{` ${rows.length}`}</span></span>
                        <span style={S.actions}>
                            <button style={S.btn} onClick={() => this._copy()}>
                                {this.state.copied ? this.t('copied') : this.t('copy')}
                            </button>
                            <button style={S.btn} onClick={() => this._clear()}>{this.t('clear')}</button>
                            <button style={S.btn} onClick={this.close}>{this.t('close')}</button>
                        </span>
                    </div>
                    <div style={S.body}>
                        {rows.length === 0 ? <div style={S.empty}>{this.t('empty')}</div> : rows.map((r, i) => {
                            const d = new Date(r.t);
                            const pad = n => `0${n}`.slice(-2);
                            return (
                                <div key={i} style={S.row}>
                                    <div style={S.when}>{`${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`}</div>
                                    <div style={S.msg}>{r.n > 1 ? `${r.m} (x${r.n})` : r.m}</div>
                                    {r.s ? <div style={S.src}>{`${r.s}:${r.l}`}</div> : null}
                                    {r.k ? <div style={S.stack}>{r.k}</div> : null}
                                </div>
                            );
                        })}
                    </div>
                    <div style={S.note}>
                        <span style={{display: 'inline-flex', marginRight: 5}} dangerouslySetInnerHTML={{__html: ICONS.lock}} />
                        {this.t('note')}
                    </div>
                </div>
            </div>
        );
    }
}

const S = {
    overlay: {position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000},
    modal: {background: '#fff', borderRadius: 12, width: 'min(640px, 94vw)', maxHeight: '82vh', display: 'flex',
        flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif'},
    head: {display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px 8px'},
    title: {fontSize: 15, fontWeight: 'bold', color: '#1f2540'},
    cnt: {color: '#9aa1b4', fontWeight: 'normal', fontSize: 12},
    actions: {display: 'flex', gap: 6},
    btn: {border: 'none', borderRadius: 14, background: '#eef0f5', color: '#4a5169', fontSize: 12,
        padding: '5px 11px', cursor: 'pointer'},
    body: {padding: '0 18px', overflow: 'auto', flex: 1},
    empty: {fontSize: 13, color: '#9aa1b4', padding: '18px 0'},
    row: {borderTop: '1px solid #eef0f5', padding: '8px 0'},
    when: {fontSize: 11, color: '#9aa1b4'},
    msg: {fontSize: 13, color: '#c0392b', wordBreak: 'break-word', marginTop: 1},
    src: {fontSize: 11, color: '#67708a', marginTop: 2},
    stack: {fontSize: 11, color: '#8a90a3', fontFamily: 'Consolas, Menlo, monospace', marginTop: 2, wordBreak: 'break-all'},
    note: {fontSize: 11, color: '#8a90a3', background: '#f6f8fb', padding: '8px 18px', borderRadius: '0 0 12px 12px'}
};

ErrorLog.propTypes = {intl: PropTypes.object};

export default injectIntl(ErrorLog);
